/* eslint-disable camelcase */
import * as React from "react"
import * as yup from "yup"
import { Appearance, ViewStyle } from "react-native"
import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps, FormikValues } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { Text } from "../../text/text"
import { Divider, View } from "native-base"
import { color, nativeBaseTheme } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { FC, useCallback, useMemo, useRef, useState } from "react"
import moment from "moment"
import { Button } from "../../button/button"
import { useStores } from "../../../models"

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10,
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4,
}

export interface CycleCloseMultipleFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number
    models?: Array<{
      id?: number
      finished_at?: string
    }>
  }
}

const CycleCloseForm: FC<{ colorScheme?: "light"|"dark", values: any, index: number } & FieldArrayRenderProps> = observer(function PondCreateForm(props) {
  const {
    colorScheme = "light" || Appearance.getColorScheme(),
    name,
    index,
    form: { values, setFieldValue, setFieldTouched }
  } = props;

  const [ cycle, setCycle ] = useState<any>(null)

  return <>
    <ModelField
      key={`${values.farm_id}-${name}-${index}-id`}
      name={`${name}.${index}.id`}
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true, labelTx: "cycleForm.pondId" }}
      modelPickerProps={{
        Model: Cycle,
        dataLabel: "finished_at",
        placeholderTx: "cycleForm.pondIdPlaceholder",
        fetchData: (model, search) => (
          model.fetch({
            farm_id__eq: values.farm_id,
            finished_at__null: 1,
            search,
            sort: "started_at,desc|pond_name_alpha_numeric",
          })
        ),
        onValueChange: async (value, i, item) => {
          await setFieldValue(`${name}.${index}.id`, value || null)
          await setFieldTouched(`${name}.${index}.id`)
          setCycle(item)
        },
        renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
        clearable: true,
        isDisabled: !values.farm_id,
        titleTx: "cycleForm.pondIdPlaceholder"
      }}
    />
    <DateTimePickerField
      key={`${values.farm_id}-${name}-${index}-id-${(cycle || {}).id}`}
      name={`${name}.${index}.finished_at`}
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      dateTimePickerProps={{
        dateTimePickerProps: {
          minimumDate: cycle && moment(cycle.started_at).add(1, 'day').toDate() || undefined,
          maximumDate: cycle && moment(cycle.started_at).add(6, 'months').toDate() || undefined
        }
      }}
    />
  </>
});

const FormikField: React.FC<{ colorScheme?: "light"|"dark" } & FormikProps<any>> = observer(function FormikField(props) {
  const { colorScheme, initialValues, setFieldValue, setFieldTouched } = props
  const { appStore } = useStores()

  const renderFieldArray = useCallback((props: FieldArrayRenderProps) => {
    const { name, push, remove, form: { values } } = props

    return <>
      {(values.models || []).map((model, index) => <View key={`${values.farm_id}-${name}-${index}`}>
        <CycleCloseForm
          colorScheme={colorScheme}
          values={model}
          index={index}
          {...props}
        />
        {values[name].length > 1 && <View alignItems="flex-end" paddingBottom="8px" paddingX="16px">
          <Button
            testID="jala:id/delete-button"
            tx="delete"
            textPreset="title4"
            preset="link"
            colorScheme={colorScheme}
            textColor={nativeBaseTheme.colors.danger["500"]}
            onPress={() => remove(index)}
          />
        </View>}
        <Divider />
      </View>)}
      <View alignItems="flex-end" paddingY="8px" paddingX="16px">
        <Button
          testID="jala:id/add-button"
          tx="cycleForm.nextPond"
          textPreset="title4"
          preset="link"
          colorScheme={colorScheme}
          onPress={() => push({
            finished_at: moment().format('YYYY-MM-DD')
          })}
        />
      </View>
    </>
  }, [colorScheme])

  const fetchFarmData = useCallback((model, search) => Promise.resolve(
    appStore.farms.filter((f) => f.name.toUpperCase().includes((search || '').toUpperCase()))
      .map((f) => Farm.hydrate(f))
  ), [appStore])

  return <>
    <ModelField
      name="farm_id"
      txPrefix="cycleForm"
      colorScheme={colorScheme}
      formControlProps={{ isRequired: true }}
      modelPickerProps={{
        Model: Farm,
        isDisabled: !!initialValues.farm_id,
        onValueChange: async (value) => {
          await setFieldValue("models", [])
          await setFieldValue("farm_id", value || null)
          await setFieldTouched("farm_id")
        },
        fetchData: fetchFarmData,
      }}
    />
    <View style={TITLE}>
      <Text tx="cycleForm.cycleClose" preset="title3" colorScheme={colorScheme} />
    </View>
    <View style={BORDER} />
    <FieldArray
      name="models"
      render={renderFieldArray}/>
  </>
})

/**
 * Describe your component here
 */
export const CycleCloseMultipleForm = observer(function CycleCloseMultipleForm(props: CycleCloseMultipleFormProps) {
  const {
    initialValues: initialValuesProps = {}, onSubmit, colorScheme = Appearance.getColorScheme(), innerRef: innerRefProps,
    ...rest
  } = props

  const modelForm = useRef<FormikProps<FormikValues>>()
  const innerRef = useCallback((node) => {
    modelForm.current = node;
    if (innerRefProps instanceof Function)
      innerRefProps(node);
  }, [innerRefProps])

  // const input = {
  //   //
  // }

  // const nextInput = {
  //   //
  // }

  const validationSchema = useMemo(() => yup.object({
    farm_id: yup.number().nullable().required().label(translate("cycleForm.farmId")),
    models: yup.array().of(yup.object({
      id: yup.lazy((value, options: any) => {
        return yup.number()
          .notOneOf(
            modelForm.current && modelForm.current.values.models.map(m => m.id).filter((m, i) => i !== options.index) || [],
            translate('yup.array.unique')
          )
          .nullable().required()
          .label(translate("cycleForm.id"))
      }),
      finished_at: yup.mixed().nullable().required().label(translate("cycleForm.finishedAt")),
    })).nullable().required(),
  }), [])

  const initialValues = useMemo<CycleCloseMultipleFormProps["initialValues"]>(() => {
    const models = initialValuesProps.models || [];
    return ({
      farm_id: null,
      ...initialValuesProps,
      models: models.length ? models : [{
        finished_at: moment().format('YYYY-MM-DD')
      }]
    })
  }, [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {(formikProps) => (
        <FormikField colorScheme={colorScheme} {...formikProps} />
      )}
    </Formik>
  )
})

CycleCloseMultipleForm.displayName = 'CycleCloseMultipleForm'
